import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
  alpha,
} from '@mui/material';

/**
 * Compact status strip + award dialog for daily login / weekly chest.
 */
export default function DailyLoginBonusBanner({
  dailyLoginBonus,
  weeklyLoginChest,
  awardOpen,
  onCloseAward,
  font = "'Montserrat', sans-serif",
  colors = {},
}) {
  const T = {
    text: '#FAFAFA',
    textSecondary: '#A1A1AA',
    textMuted: '#71717A',
    accent: '#E4FF1A',
    success: '#34D399',
    border: 'rgba(255,255,255,0.06)',
    surface: '#111113',
    ...colors,
  };

  if (!dailyLoginBonus?.enabled) return null;

  const streak = Number(dailyLoginBonus.streak || 0);
  const nextAmount = Number(dailyLoginBonus.nextAmount || 0);
  const chest = weeklyLoginChest || dailyLoginBonus.weeklyChest || {};
  const required = Number(chest.requiredStreakDays || 7);
  const chestProgress = Math.min(100, Math.round((streak / Math.max(1, required)) * 100));

  let chestLabel = `Chest unlocks at day ${required}`;
  if (chest.claimedThisWeek) chestLabel = 'Weekly chest claimed';
  else if (chest.eligible) chestLabel = `Chest ready · ${chest.range?.min ?? '—'}–${chest.range?.max ?? '—'} tokens`;
  else if (streak > 0) chestLabel = `Chest progress · day ${streak}/${required}`;

  const awardedDaily = Boolean(dailyLoginBonus.awarded && dailyLoginBonus.amount > 0);
  const awardedChest = Boolean(weeklyLoginChest?.awarded && weeklyLoginChest.amount > 0);

  return (
    <>
      <Alert
        severity="success"
        icon={false}
        sx={{
          mb: 2,
          bgcolor: alpha(T.success, 0.08),
          border: `1px solid ${alpha(T.success, 0.25)}`,
          color: T.text,
          '& .MuiAlert-message': { width: '100%' },
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
          >
            <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: '0.95rem' }}>
              Daily login · Day {streak || 1}
              {dailyLoginBonus.claimedToday
                ? ` · claimed (+${Number(dailyLoginBonus.todayAmount || dailyLoginBonus.amount || 0).toLocaleString()} tokens)`
                : ''}
            </Typography>
            <Typography sx={{ color: T.textSecondary, fontSize: '0.85rem' }}>
              {dailyLoginBonus.claimedToday
                ? `Tomorrow · +${nextAmount.toLocaleString()} tokens`
                : `Today · +${nextAmount.toLocaleString()} tokens`}
            </Typography>
          </Stack>
          {chest.enabled !== false && (
            <Box>
              <Typography sx={{ color: T.textMuted, fontSize: '0.8rem', mb: 0.5 }}>
                {chestLabel}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={chest.claimedThisWeek ? 100 : chestProgress}
                sx={{
                  height: 6,
                  borderRadius: 1,
                  bgcolor: alpha(T.accent, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: T.accent },
                }}
              />
            </Box>
          )}
        </Stack>
      </Alert>

      <Dialog
        open={Boolean(awardOpen && (awardedDaily || awardedChest))}
        onClose={onCloseAward}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: font, fontWeight: 700, color: T.text }}>
          Login rewards
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {awardedDaily && (
              <Typography sx={{ color: T.text, fontFamily: font }}>
                Day {dailyLoginBonus.streak}: +{Number(dailyLoginBonus.amount).toLocaleString()} tokens
              </Typography>
            )}
            {awardedChest && (
              <Typography sx={{ color: T.accent, fontFamily: font, fontWeight: 700 }}>
                Weekly chest: +{Number(weeklyLoginChest.amount).toLocaleString()} tokens!
              </Typography>
            )}
            {awardedDaily && (
              <Typography sx={{ color: T.textSecondary, fontSize: '0.9rem' }}>
                Come back tomorrow for +{Number(dailyLoginBonus.nextAmount || 0).toLocaleString()} tokens.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCloseAward} variant="contained" sx={{ textTransform: 'none', fontFamily: font }}>
            Nice
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
